import { ControlSheetRunDTO, ControlSheetTripPairDTO, TripDTO } from '@/types/controlsheet'
import { timeToMinutes } from './breakpointTimeUtils'
import { standardizeTime } from './standardizeTime'
import { isTimeBefore } from './breakpointTimeUtils'

/**
 * Compares two objects for deep equality, ignoring the specified keys.
 * Uses JSON serialization for comparison, so object key order matters.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepEqualExcept<T extends Record<string, any>>(obj1: T, obj2: T, excludeKeys: (keyof T)[]): boolean {
    const rest1 = { ...obj1 }
    const rest2 = { ...obj2 }

    excludeKeys.forEach((key) => {
        delete rest1[key]
        delete rest2[key]
    })

    return JSON.stringify(rest1) === JSON.stringify(rest2)
}

/**
 * Removes duplicate items from an array using deep equality comparison,
 * ignoring the specified keys when comparing. Keeps the first occurrence.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dedupeByDeepEquality<T extends Record<string, any>>(array: T[], excludeKeys: (keyof T)[]): T[] {
    const result: T[] = []

    for (const item of array) {
        const isDuplicate = result.some((existing) => deepEqualExcept(existing, item, excludeKeys))

        if (!isDuplicate) {
            result.push(item)
        }
    }

    return result
}

export interface PairTripsResult {
    run: ControlSheetRunDTO
    nextTripMap: Map<TripDTO, TripDTO>
}

/**
 * Re-pairs trips within a run by flattening and re-sequencing them.
 *
 * The backend returns trip pairs per run, but the pairing can be incorrect —
 * the same trip may appear in multiple pairs, or outbound/inbound pairs may
 * not be sequentially adjacent. This function fixes that by:
 *
 * 1. Flattening all outbound and inbound trips from the existing pairs into
 *    a single list, sorted by trip number (ascending).
 * 2. Deduplicating trips that are identical except for `spell`/`spellTime`
 *    (which can differ between duplicate entries from the backend).
 * 3. Re-pairing sequentially: walks through the sorted list and pairs each
 *    outbound (direction 'o') with the immediately following inbound
 *    (direction 'i'). Unpaired outbounds or inbounds become single-sided pairs.
 *
 * Pairing rules (applied in order):
 * 1. Outbound with `runNextTrip` pointing to an inbound in the array → paired together
 * 2. Outbound with `runNextTrip` null (or target not found) → outbound-only pair
 * 3. Inbound trips not claimed by any outbound → inbound-only pair
 * 4. Second pass: remaining outbound-only pairs are matched with remaining
 *    inbound-only pairs if the outbound's arriveStandTime is up to 60 minutes
 *    before the inbound's departStandTime
 * 5. Any pairs left with both outbound and inbound null are removed
 *
 * @returns A PairTripsResult with corrected trip pairs and a nextTripMap linking
 *          each trip to its successor in the sorted order.
 */
export const pairTripsProperly = (run: ControlSheetRunDTO): PairTripsResult => {
    const outbounds = run.tripPairs.map((pair) => pair.outbound)
    const inbounds = run.tripPairs.map((pair) => pair.inbound)

    // Flatten all trips into a single sorted list by trip number
    const allTrips = [...outbounds, ...inbounds]
        .filter((trip) => trip !== null)
        .filter((trip) => {
            return !isTimeBefore(standardizeTime(trip.arriveStandTime), standardizeTime(trip.vStartTime))
        })
        .sort((a, b) => {
            if (!a || !b) return 0
            const tripDiff = Number(a.trip) - Number(b.trip)
            if (tripDiff !== 0) return tripDiff
            // Same trip number, same direction: use vStartTime vs departStandTime to
            // determine ordering for correct pairing.
            //
            // Outbounds: mid-trip takeovers first — when a from-terminus duty is relieved
            // mid-trip, both duties may share the same runNextTrip; the taking-over duty
            // is the one that continues to the inbound and should claim it.
            //
            // Inbounds: from-terminus first — the duty starting from the inbound stand
            // should be claimed by the paired outbound; the mid-trip takeover becomes
            // an inbound-only row below.
            const aIsMidTrip =
                !!a.vStartTime && isTimeBefore(standardizeTime(a.departStandTime), standardizeTime(a.vStartTime))
            const bIsMidTrip =
                !!b.vStartTime && isTimeBefore(standardizeTime(b.departStandTime), standardizeTime(b.vStartTime))
            if (a.direction !== 'i' && b.direction !== 'i') {
                // Both outbounds: mid-trip takeover first
                if (aIsMidTrip && !bIsMidTrip) return -1
                if (!aIsMidTrip && bIsMidTrip) return 1
            } else if (a.direction === 'i' && b.direction === 'i') {
                // Both inbounds: from-terminus first (mid-trip takeover last)
                if (!aIsMidTrip && bIsMidTrip) return -1
                if (aIsMidTrip && !bIsMidTrip) return 1
            }
            return 0
        })

    // Remove duplicates that differ only in spell/spellTime (a backend artefact)
    const dedupedTrips = dedupeByDeepEquality(allTrips, ['spell', 'spellTime'])

    const nextTripMap = new Map<TripDTO, TripDTO>()
    for (let i = 0; i < dedupedTrips.length - 1; i++) {
        nextTripMap.set(dedupedTrips[i], dedupedTrips[i + 1])
    }

    const tripPairs = [] as ControlSheetTripPairDTO[]

    // Pre-populate the set of specific inbound TripDTO objects that will be claimed
    // by an outbound's runNextTrip. Using object identity (not trip number strings)
    // so that when multiple inbound trips share the same trip number (e.g. two
    // inbound trip-26s for different duties), only the one actually targeted is
    // reserved — the others remain free to be emitted as inbound-only pairs.
    const reservedForOutbound = new Set<TripDTO>()
    for (const trip of dedupedTrips) {
        if (trip.direction !== 'i' && trip.runNextTrip) {
            // Prefer the inbound whose duty matches the outbound's duty, so that
            // e.g. outbound-566 pairs with inbound-566-R rather than inbound-554.
            const targetInbound =
                dedupedTrips.find(
                    (t) =>
                        t.trip === trip.runNextTrip &&
                        t.direction === 'i' &&
                        t.duty === trip.duty &&
                        !reservedForOutbound.has(t)
                ) ??
                dedupedTrips.find(
                    (t) =>
                        t.trip === trip.runNextTrip &&
                        t.direction === 'i' &&
                        !reservedForOutbound.has(t) &&
                        // Don't reserve mid-trip inbound takeovers — they should be
                        // inbound-only rows, not paired with a mismatched outbound.
                        !(
                            t.vStartTime &&
                            isTimeBefore(standardizeTime(t.departStandTime), standardizeTime(t.vStartTime))
                        )
                )
            if (targetInbound) {
                reservedForOutbound.add(targetInbound)
            }
        }
    }

    // Tracks which specific inbound objects have already been claimed in the walk
    // below. Using object identity (not trip number strings) so that multiple
    // inbounds with the same trip number (e.g. two inbound trip 250s for two
    // different duties) are each claimed by a distinct outbound.
    const claimedInboundObjects = new Set<TripDTO>()

    // Walk through sorted trips and pair each outbound with its inbound
    for (let i = 0; i < dedupedTrips.length; i += 1) {
        const trip = dedupedTrips[i] as TripDTO

        // Skip inbound trips reserved for a specific outbound's runNextTrip claim
        if (reservedForOutbound.has(trip)) {
            continue
        }

        // Inbound trips that are not reserved become inbound-only pairs
        if (trip.direction === 'i') {
            tripPairs.push({ outbound: null, inbound: trip })
            continue
        }

        // Trip is outbound — use runNextTrip to determine pairing
        if (trip.runNextTrip) {
            // Prefer the inbound whose duty matches the outbound's duty; fall back
            // to the first available inbound if no same-duty match exists.
            const targetInbound = (dedupedTrips.find(
                (t) =>
                    t.trip === trip.runNextTrip &&
                    t.direction === 'i' &&
                    t.duty === trip.duty &&
                    !claimedInboundObjects.has(t)
            ) ??
                dedupedTrips.find(
                    (t) =>
                        t.trip === trip.runNextTrip &&
                        t.direction === 'i' &&
                        !claimedInboundObjects.has(t) &&
                        // Don't pair with a mid-trip inbound takeover — those become inbound-only rows.
                        !(
                            t.vStartTime &&
                            isTimeBefore(standardizeTime(t.departStandTime), standardizeTime(t.vStartTime))
                        )
                )) as TripDTO | undefined
            if (targetInbound) {
                tripPairs.push({ outbound: trip, inbound: targetInbound })
                claimedInboundObjects.add(targetInbound)
                continue
            }
        }

        // runNextTrip is null or target not found — outbound has no inbound pair
        tripPairs.push({ outbound: trip, inbound: null })
    }

    // Second pass: pair unpaired outbound-only rows with unpaired inbound-only rows
    // if the outbound's arriveStandTime is up to 60 minutes before the inbound's departStandTime
    const outboundOnlyIndices = tripPairs
        .map((pair, idx) => (pair.outbound && !pair.inbound ? idx : -1))
        .filter((idx) => idx !== -1)
    const inboundOnlyIndices = tripPairs
        .map((pair, idx) => (!pair.outbound && pair.inbound ? idx : -1))
        .filter((idx) => idx !== -1)

    const consumedInboundIndices = new Set<number>()

    for (const outIdx of outboundOnlyIndices) {
        const outbound = tripPairs[outIdx].outbound!
        if (!outbound.arriveStandTime) continue

        const outboundArrival = timeToMinutes(outbound.arriveStandTime)

        for (const inIdx of inboundOnlyIndices) {
            if (consumedInboundIndices.has(inIdx)) continue

            const inbound = tripPairs[inIdx].inbound!
            if (!inbound.departStandTime) continue

            // Don't pair with a mid-trip inbound takeover — those become inbound-only rows.
            if (inbound.vStartTime && isTimeBefore(standardizeTime(inbound.departStandTime), standardizeTime(inbound.vStartTime))) {
                continue
            }

            const inboundDepart = timeToMinutes(inbound.departStandTime)
            const gap = inboundDepart - outboundArrival

            if (gap >= 0 && gap <= 60) {
                // Merge: assign the inbound to the outbound-only pair
                tripPairs[outIdx] = { outbound, inbound }
                // Mark the inbound-only pair for removal
                tripPairs[inIdx] = { outbound: null, inbound: null }
                consumedInboundIndices.add(inIdx)
                break
            }
        }
    }

    // Remove pairs where both outbound and inbound are null
    const cleanedPairs = tripPairs.filter((pair) => pair.outbound || pair.inbound)

    return {
        run: {
            ...run,
            tripPairs: cleanedPairs,
        },
        nextTripMap,
    }
}
